import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {EditTwoTone, InfoCircleOutlined, PlusOutlined} from '@ant-design/icons';

import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  rackCategoryList,
  addRackCategory,
  updateRackCategory,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  rackCategoryStatusFilter,
} from './rackCategorySlice';
import type {ADD_RACK_CATEGORY, UPDATE_RACK_CATEGORY, RACK_CATEGORY} from './rackCategory.type';
import {formatDate} from '../../../../utils/formatDate';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {sortbyFilter, statusFilter, statusFilterOptions} from '../../../../common/constants';

const RackCategory = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.rackCategory);
  const userPermission = useAppSelector((state) => state.config.config.permissions);
  const [isRackCategorModalOpen, setIsRackCategorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [rackCategoryForm] = Form.useForm();

  const [modalData, setModalData] = useState<RACK_CATEGORY>({} as RACK_CATEGORY);

  const handleUpdateRackCategory = (values: UPDATE_RACK_CATEGORY) => {
    const payload = checkFormUpdate(values, modalData);

    if (Object.keys(payload).length > 0) {
      dispatch(updateRackCategory({payload: payload, category_id: modalData.id}))
        .unwrap()
        .then(() => {
          setIsRackCategorModalOpen(false);
          dispatch(
            rackCategoryList({
              page_size: state.rackCategories.page_size,
              page_number: state.rackCategories.page_number,
              sort_by: state.rackCategories.sort_by,
              ascending: state.rackCategories.ascending,
              search: state.rackCategories.search,
            }),
          );
        });
      // .catch((error) => {});
    } else {
      message.warning('No fields are updated');
    }
  };

  const handleAddRackCategory = (values: ADD_RACK_CATEGORY) => {
    dispatch(addRackCategory(values))
      .unwrap()
      .then(() => {
        setIsRackCategorModalOpen(false);
        dispatch(
          rackCategoryList({
            page_size: state.rackCategories.page_size,
            page_number: state.rackCategories.page_number,
            sort_by: state.rackCategories.sort_by,
            ascending: state.rackCategories.ascending,
            search: state.rackCategories.search,
          }),
        );
      });
    // .catch((error) => {});
  };

  useEffect(() => {
    //
    dispatch(
      rackCategoryList({
        page_size: state.rackCategories.page_size,
        page_number: state.rackCategories.page_number,
        sort_by: state.rackCategories.sort_by,
        ascending: state.rackCategories.ascending,
        search: state.rackCategories.search,
        is_active: state.rackCategories.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.rackCategories.page_number,
    state.rackCategories.page_size,
    state.rackCategories.search,
    state.rackCategories.is_active,
    state.rackCategories.sort_by,
    state.rackCategories.ascending,
  ]);

  const columns: ColumnsType<RACK_CATEGORY> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return (
          <>{(state.rackCategories.page_number - 1) * state.rackCategories.page_size + index + 1}</>
        );
      },
    },
    {
      title: ' Name',
      dataIndex: 'name',
      sorter: true,
    },

    {
      title: 'Varient',
      dataIndex: 'varient',
    },
    {
      title: 'Cooling',
      dataIndex: 'cooling_technology',
    },
    {
      title: 'Chargers',
      dataIndex: 'no_of_charger',
    },

    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active === true ? (
          <Tag color='green'>Active</Tag>
        ) : (
          <Tag color='red'>Inactive</Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {userPermission.update_rackcategory ? (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  setModalData(record);
                  setIsUpdateModal(true);
                  rackCategoryForm.setFieldsValue({
                    name: record.name,
                    no_of_charger: record.no_of_charger,
                    max_power: record.max_power,
                    max_grid_power: record.max_grid_power,
                    max_solar_power: record.max_solar_power,
                    cooling_technology: record.cooling_technology,
                    varient: record.varient,
                    is_active: record.is_active,
                  });
                  setIsRackCategorModalOpen(true);
                }}
              />
            ) : null}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px 1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={state.rackCategories.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            state.rackCategories.is_active === undefined
              ? statusFilter.ALL
              : state.rackCategories.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(rackCategoryStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(rackCategoryStatusFilter(undefined));
            } else {
              dispatch(rackCategoryStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {userPermission.add_rackcategory && (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsUpdateModal(false);
                rackCategoryForm.resetFields();
                setIsRackCategorModalOpen(true);
              }}
            >
              Add Category
            </Button>
          )}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.rackCategories.totalDataCount,
            current: state.rackCategories.page_number,
            pageSize: state.rackCategories.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.rackCategories.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.rackCategories.pageData}
        scroll={{x: undefined, y: '60vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.rackCategories.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.rackCategories.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      <Modal
        title={`${modalData.name}`}
        open={isInfoModalOpen}
        onCancel={() => {
          setIsInfoModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label=' Name' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.name}
          </Descriptions.Item>
          <Descriptions.Item label='Status' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.is_active ? (
              <Tag color='green'>Active</Tag>
            ) : (
              <Tag color='red'>Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label='Created By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.created_by}
          </Descriptions.Item>
          <Descriptions.Item label='Created At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Updated By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.updated_by}
          </Descriptions.Item>
          <Descriptions.Item label='Updated At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label=' Varient' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.varient}
          </Descriptions.Item>
          <Descriptions.Item label='Cooling Technology' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.cooling_technology}
          </Descriptions.Item>
          <Descriptions.Item label=' No. of Chargers' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.no_of_charger}
          </Descriptions.Item>
          <Descriptions.Item label=' Max. Power (Watt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.max_power}
          </Descriptions.Item>
          <Descriptions.Item label=' Max. Grid Power (Watt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.max_grid_power}
          </Descriptions.Item>
          <Descriptions.Item label=' Max. Solar Power (Watt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.max_solar_power}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      <Modal
        title={isUpdateModal ? 'Update Cabinet Category' : 'Add Cabinet Category'}
        open={isRackCategorModalOpen}
        maskClosable={false}
        onCancel={() => {
          // rackCategoryForm.resetFields();
          setIsRackCategorModalOpen(false);
        }}
        okText={isUpdateModal ? 'Update Category' : 'Add Category'}
        confirmLoading={state.isLoading}
        onOk={() => {
          rackCategoryForm
            .validateFields()
            .then((values) => {
              //rackCategoryForm.resetFields();
              {
                isUpdateModal ? handleUpdateRackCategory(values) : handleAddRackCategory(values);
              }
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='rackCategory' form={rackCategoryForm} layout='vertical'>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='name'
                    label='Category Name'
                    rules={[
                      {required: true, message: 'Please fill your full Name '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='varient'
                    label='Varient '
                    rules={[
                      {required: true, message: 'Please fill Varient  '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='no_of_charger'
                    label='No of Charger '
                    rules={[{required: true, message: 'Please fill a value '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='max_power'
                    label={
                      <>
                        Max Power <span style={{color: '#1890ff'}}>(Watt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Max. Power '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='max_grid_power'
                    label={
                      <>
                        Max Grid Power <span style={{color: '#1890ff'}}>(Watt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Grid Power '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='max_solar_power'
                    label={
                      <>
                        Max Solar Power <span style={{color: '#1890ff'}}>(Watt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Max. Solar Power '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='cooling_technology'
                    label='Cooling Technology'
                    rules={[{required: true, message: 'Please fill Coolin Technology '}]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  {isUpdateModal && (
                    <Form.Item name='is_active' label='Status' valuePropName='checked'>
                      <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default RackCategory;
